// Offline scanning support for weak-signal routes (hill-station operator buses).
//
// Two pieces of local state, persisted via nativeStore (native storage in the APK, so they
// survive the switch to the bundled fallback origin; localStorage on the web):
//   - scanix_offline_cache: the last-synced per-student payment snapshot, used to make
//     allowed/blocked calls when /api/scan can't be reached.
//   - scanix_offline_queue: scans recorded while offline, waiting to be POSTed to
//     /api/attendance/sync-offline once signal returns.
//
// All functions are async because native storage is async.

import { storeGetJSON, storeSetJSON } from "./nativeStore";

const CACHE_KEY = "scanix_offline_cache";
const QUEUE_KEY = "scanix_offline_queue";
const SYNC_CHUNK_SIZE = 100;

// ---------------- Eligibility cache ----------------

export async function getCache() {
    return storeGetJSON(CACHE_KEY, null); // { generatedAt, byToken: { [qr_token]: {...} } }
}

/** Best-effort — swallow any failure (this is expected to be called opportunistically,
 * whenever there happens to be signal, not as something the caller must handle). */
export async function refreshCache(api) {
    try {
        const { data } = await api.get("/offline/eligibility-snapshot", { timeout: 8000 });
        const byToken = {};
        for (const s of data.students || []) byToken[s.qr_token] = s;
        await storeSetJSON(CACHE_KEY, { generatedAt: data.generated_at, byToken });
        return true;
    } catch {
        return false;
    }
}

export async function lookupToken(qrToken) {
    const cache = await getCache();
    return cache?.byToken?.[qrToken] || null;
}

// ---------------- Offline decision ----------------

function uuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    // Fallback for older WebViews without crypto.randomUUID
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/** Mirrors /api/scan's response shape ({result, message, student, attendance}) so ResultSheet
 * and the rest of Scan.jsx need near-zero branching for the offline case. */
export async function decideOffline(qrToken, bus, user) {
    const cached = await lookupToken(qrToken);
    const scannedAt = new Date().toISOString();
    const clientScanId = uuid();

    let result, message, student, needsReview = false;

    if (!cached) {
        // Unknown to the last-synced cache — new student, or a bad code. Decided with the
        // client: fail open (allow boarding) rather than strand a possibly-legitimate rider;
        // flag it so admin reviews it once synced.
        result = "allowed";
        message = "Unknown QR — offline, unable to verify against cached records. Flagged for review.";
        student = null;
        needsReview = true;
    } else if (!cached.active_status) {
        result = "blocked";
        message = "Student inactive (cached)";
        student = cached;
    } else if (cached.eligible) {
        result = "allowed";
        message = "Payment verified for all cycles (cached)";
        student = cached;
    } else {
        result = "blocked";
        const labels = (cached.unpaid_months || []).join(", ");
        message = `Payment due: ${labels} (cached)`;
        student = cached;
    }

    const attendance = {
        client_scan_id: clientScanId,
        qr_token: qrToken,
        bus_id: bus.id,
        student_id: cached?.student_id || null,
        student_name: cached?.name || null,
        student_roll: cached?.roll_number || null,
        operator_id: user?.id || null,
        operator_name: user?.name || null,
        // Mirrors online /scan's own field: payment status alone, independent of active_status
        // (cached.eligible gates boarding on *both*, so using it here would mislabel an
        // inactive-but-fully-paid student as "due"). Unknown for the unrecognized-token case —
        // there's nothing cached.
        payment_status_at_scan: cached ? (cached.unpaid_months.length === 0 ? "paid" : "due") : null,
        result,
        reason: message,
        needs_review: needsReview,
        scanned_at: scannedAt,
    };

    return {
        offline: true,
        needsReview,
        result,
        message,
        student: student
            ? {
                  name: student.name,
                  roll_number: student.roll_number,
                  department: student.department,
              }
            : null,
        attendance,
    };
}

// ---------------- Sync queue ----------------

export async function getQueue() {
    return storeGetJSON(QUEUE_KEY, []);
}

export async function enqueueScan(attendance) {
    const queue = await getQueue();
    queue.push(attendance);
    await storeSetJSON(QUEUE_KEY, queue);
    return queue.length;
}

async function removeFromQueue(ids) {
    const idSet = new Set(ids);
    const remaining = (await getQueue()).filter((item) => !idSet.has(item.client_scan_id));
    await storeSetJSON(QUEUE_KEY, remaining);
    return remaining;
}

/** Flushes the queue to the backend. Safe to call opportunistically/repeatedly — synced and
 * already-synced (duplicate) items are both cleared locally; anything else stays queued for
 * the next attempt. Returns the number of items cleared from the queue. */
export async function syncQueue(api) {
    const queue = await getQueue();
    if (queue.length === 0) return 0;

    let clearedTotal = 0;
    for (let i = 0; i < queue.length; i += SYNC_CHUNK_SIZE) {
        const chunk = queue.slice(i, i + SYNC_CHUNK_SIZE);
        try {
            const { data } = await api.post(
                "/attendance/sync-offline",
                { scans: chunk },
                { timeout: 15000 }
            );
            const cleared = [...(data.synced || []), ...(data.duplicates || [])];
            await removeFromQueue(cleared);
            clearedTotal += cleared.length;
        } catch {
            // Network still down (or server error) — leave this and remaining chunks queued,
            // try again next opportunity.
            break;
        }
    }
    return clearedTotal;
}

/** True for an axios error that means "couldn't reach the server" (no signal, DNS failure,
 * timeout) — as opposed to a real response from the server (4xx/5xx), which should be
 * surfaced normally rather than triggering the offline fallback. */
export function isNetworkError(err) {
    return !err?.response;
}
